import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["options", "use"];
/* eslint-disable hubspot-dev/no-private-classes */
import { uniqueId } from 'UIComponents/utils/underscore';
import { DropdownCaret } from 'foundations-components/transitional/components/AbstractDropdown';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { DropdownDivider } from 'foundations-components/transitional/components/Dropdown';
import { Select } from 'foundations-components/transitional/components/Select';
import { isOptionGroup } from 'foundations-components/transitional/types';
import styled from 'foundations-theming/utils';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledItemWrapper = styled.div.withConfig({
  displayName: "TextToolbarSelect__StyledItemWrapper"
})(({
  theme
}) => ({
  transition: 'background-color 150ms ease',
  color: theme.color['icon-core-default'],
  '&:hover': {
    backgroundColor: theme.color['fill-field-hover']
  },
  inlineSize: '100%',
  paddingBlock: theme.space[200],
  paddingInline: theme.space[300],
  textAlign: 'left',
  fontSize: theme.typography['detail-100'].fontSize,
  fontWeight: theme.typography['detail-100'].fontWeight,
  lineHeight: theme.typography['detail-100'].lineHeight
}));

// @TODO: Eventually make this the default so all consumers use this option renderer
export const TextToolbarSelectOption = ({
  children,
  disabled
}) => /*#__PURE__*/_jsx(StyledItemWrapper, {
  children: /*#__PURE__*/_jsx(Clickable, {
    disabled: disabled,
    children: children
  })
});
const hasGroups = options => options[0] && isOptionGroup(options[0]);
const flattenOptions = options => {
  if (!hasGroups(options)) return options;
  const flatOptions = [];
  options.forEach((optionGroup, i) => {
    optionGroup.options.forEach(option => flatOptions.push(option));
    if (i < options.length - 1) {
      flatOptions.push({
        disabled: true,
        isSpecial: true,
        text: '',
        value: '',
        rawNode: /*#__PURE__*/_jsx(DropdownDivider, {
          margin: "0"
        })
      });
    }
  });
  return flatOptions;
};
export class TextToolbarSelect extends PureComponent {
  constructor(props) {
    super(props);
    this.computeButtonWidth = () => {
      // Set the button width based on the widest option text.
      const {
        options,
        placeholder
      } = this.props;
      const {
        buttonWidth
      } = this.state;
      const buttonEl = document.getElementById(this._id);
      if (!buttonEl) return;
      const originalButtonElWidthStyle = buttonEl.style.width;
      buttonEl.style.width = '';
      const textEl = buttonEl.querySelector('[data-option-text]');
      if (!textEl) {
        return;
      }
      const originalTextElContents = textEl.innerHTML;
      let maxButtonWidth = 0;
      if (placeholder) {
        textEl.textContent = placeholder;
        maxButtonWidth = buttonEl.offsetWidth;
      }
      const flatOptions = this._flattenOptions(options);
      flatOptions.forEach(option => {
        textEl.textContent = option.text;
        const measuredOptionWidth = buttonEl.offsetWidth;
        maxButtonWidth = Math.max(maxButtonWidth, measuredOptionWidth);
      });
      maxButtonWidth = Math.ceil(maxButtonWidth + 1);
      if (maxButtonWidth === 0) {
        this.setState({
          buttonWidth: undefined
        });
      } else if (!buttonWidth || buttonWidth !== maxButtonWidth) {
        this.setState({
          buttonWidth: maxButtonWidth
        });
      }
      buttonEl.style.width = originalButtonElWidthStyle;
      textEl.innerHTML = originalTextElContents;
    };
    this.state = {
      buttonWidth: undefined
    };
    this._id = uniqueId('text-toolbar-select-');
    this._flattenOptions = memoizeOne(flattenOptions);
  }
  componentDidMount() {
    this.computeButtonWidth();
  }
  componentDidUpdate(prevProps) {
    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevProps.options !== this.props.options) {
      this.computeButtonWidth();
    }
  }
  render() {
    const _this$props = this.props,
      {
        options,
        use
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      buttonWidth
    } = this.state;
    const flatOptions = this._flattenOptions(options);
    return /*#__PURE__*/_jsx(Select, Object.assign({
      anchorType: "button",
      buttonSize: "small",
      buttonUse: use === 'on-dark' ? 'tertiary-extra-light' : 'tertiary-light',
      caretRenderer: /*#__PURE__*/_jsx(DropdownCaret, {
        buttonUse: "transparent"
      }),
      id: this._id,
      options: flatOptions,
      inlineSize: buttonWidth ? `${buttonWidth}px` : undefined,
      paddingInlineStart: "200",
      paddingInlineEnd: "200"
    }, rest));
  }
}
TextToolbarSelect.propTypes = {
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(PropTypes.any).isRequired,
  placeholder: PropTypes.string,
  searchable: PropTypes.bool,
  use: PropTypes.oneOf(['', 'on-dark']).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string
};
TextToolbarSelect.defaultProps = {
  searchable: false,
  use: ''
};
TextToolbarSelect.displayName = 'TextToolbarSelect';